/* Acropoli collection progressive disclosure. No fetch, copied text or HTML replacement. */
(() => {
  'use strict';
  const selector = '[data-acropoli-collection-layout="editorial-v3"][data-acropoli-description-expand]';
  const complexContent = 'img, picture, video, audio, iframe, table, details, form, input, select, textarea, button, [contenteditable]';
  const labels = {
    it: ['Leggi di più', 'Leggi di meno'],
    en: ['Read more', 'Read less'],
    de: ['Mehr lesen', 'Weniger lesen'],
    fr: ['Lire la suite', 'Réduire'],
  };
  let nextId = 0;

  function configuration(root) {
    const read = name => {
      const value = root.getAttribute(name);
      return /^(?:0|[1-9]|1[0-9]|20)$/.test(value || '') ? Number(value) : null;
    };
    const mobile = read('data-acropoli-expand-mobile-lines');
    const desktop = read('data-acropoli-expand-desktop-lines');
    return mobile === null || desktop === null ? null : { mobile, desktop };
  }

  // Range rectangles represent the real wrapped text runs. Merge inline runs on
  // the same visual line; paragraph margins and headings remain in the geometry.
  function textLines(description) {
    const rects = [];
    const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.textContent.trim()) continue;
      const style = getComputedStyle(node.parentElement);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      const range = document.createRange();
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) {
        if (rect.width < .1 || rect.height < .1) continue;
        const lineHeight = Number.parseFloat(style.lineHeight);
        if (!Number.isFinite(lineHeight)) return null;
        rects.push({ top: rect.top, bottom: rect.bottom, leading: Math.max(0, (lineHeight - rect.height) / 2) });
      }
    }
    rects.sort((a, b) => a.top - b.top || a.bottom - b.bottom);
    const lines = [];
    for (const rect of rects) {
      const last = lines[lines.length - 1];
      if (last && rect.top < last.bottom - 1 && rect.bottom > last.top + 1) {
        last.top = Math.min(last.top, rect.top);
        last.bottom = Math.max(last.bottom, rect.bottom);
        last.leading = Math.max(last.leading, rect.leading);
      } else lines.push({ ...rect });
    }
    return lines;
  }

  function enhance(root) {
    if (root.hasAttribute('data-acropoli-expand-initialized')) return;
    const config = configuration(root);
    const description = root.querySelector('.acropoli-collection-description');
    // Complex embedded content stays fully visible. Its own controls and media
    // must never be cut by a text-line budget.
    if (!config || !description || !description.textContent.trim() ||
        description.querySelector(complexContent) ||
        getComputedStyle(root).getPropertyValue('--acropoli-description-expand-ready').trim() !== '1') return;

    const parent = description.parentNode;
    const originalId = description.getAttribute('id');
    const column = document.createElement('div');
    column.className = 'acropoli-collection-description-column';
    const button = document.createElement('button');
    button.className = 'acropoli-collection-read-more';
    button.type = 'button';
    button.hidden = true;
    let expandedByUser = false;
    let animationFrame = 0;
    let observer;
    let mutationObserver;
    let disposed = false;
    let previousWidth = -1;
    const locale = (document.documentElement.lang || 'it').toLowerCase().split('-')[0];
    const copy = labels[locale] || labels.it;

    function failOpen() {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      document.fonts?.removeEventListener?.('loadingdone', schedule);
      document.removeEventListener('shopify:section:unload', unload);
      description.removeEventListener('focusin', focusDescription);
      description.style.removeProperty('--acropoli-description-collapsed-height');
      if (originalId === null) description.removeAttribute('id');
      if (column.parentNode) column.replaceWith(description);
      else if (!description.parentNode) parent.append(description);
      root.removeAttribute('data-acropoli-expand-initialized');
    }

    function update() {
      if (disposed) return;
      if (!root.isConnected || description.querySelector(complexContent)) { failOpen(); return; }
      if (getComputedStyle(root).getPropertyValue('--acropoli-description-expand-ready').trim() !== '1') { failOpen(); return; }
      const width = description.getBoundingClientRect().width;
      if (width < 1) return;
      const budget = window.innerWidth < 768 ? config.mobile : config.desktop;
      const lines = textLines(description);
      if (!lines) { failOpen(); return; }
      const overflowing = budget > 0 && lines.length > budget;
      if (overflowing) {
        const line = lines[budget - 1];
        const next = lines[budget];
        const bottom = Math.min(line.bottom + line.leading, next.top - 1);
        // Ambiguous overlapping line boxes fail open instead of cutting glyphs.
        if (bottom < line.bottom || !Number.isFinite(bottom)) { failOpen(); return; }
        const height = bottom - description.getBoundingClientRect().top;
        description.style.setProperty('--acropoli-description-collapsed-height', `${height}px`);
      }
      const expanded = !overflowing || expandedByUser;
      // Install the working control before opting into the clipping CSS.
      button.hidden = !overflowing;
      button.textContent = expanded ? copy[1] : copy[0];
      button.setAttribute('aria-expanded', String(expanded));
      column.dataset.acropoliDescriptionState = expanded ? 'expanded' : 'collapsed';
      column.dataset.acropoliVisibleLines = String(budget);
      column.dataset.acropoliTotalLines = String(lines.length);
    }

    function guardedUpdate() { try { update(); } catch { failOpen(); } }
    function unload(event) { if (event.target === root || event.target.contains?.(root)) failOpen(); }
    function schedule() {
      if (disposed) return;
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(guardedUpdate);
    }
    function focusDescription() {
      // Synchronous expansion precedes paint/automatic focus scrolling, so Tab
      // never leaves focus on a clipped link. No tabindex or aria-hidden rewrite.
      if (column.dataset.acropoliDescriptionState === 'collapsed') {
        expandedByUser = true;
        guardedUpdate();
      }
    }

    try {
      if (!description.id) {
        do { nextId += 1; description.id = `acropoli-collection-description-${nextId}`; }
        while (document.getElementById(description.id) !== description);
      }
      button.setAttribute('aria-controls', description.id);
      description.before(column);
      column.append(description, button);
      button.addEventListener('click', () => {
        const wasExpanded = column.dataset.acropoliDescriptionState === 'expanded';
        const oldTop = button.getBoundingClientRect().top;
        expandedByUser = !wasExpanded;
        guardedUpdate();
        if (wasExpanded && !disposed) {
          button.focus({ preventScroll: true });
          const newTop = button.getBoundingClientRect().top;
          // Keep the activated control in place while removing the expanded
          // height; instant scrolling also respects reduced-motion preferences.
          if (oldTop >= 0 && oldTop < window.innerHeight) window.scrollBy({ top: newTop - oldTop, behavior: 'instant' });
          requestAnimationFrame(() => {
            if (disposed) return;
            const box = button.getBoundingClientRect();
            if (box.top < 0 || box.bottom > window.innerHeight) button.scrollIntoView({ block: 'nearest', behavior: 'instant' });
          });
        }
      });
      description.addEventListener('focusin', focusDescription);
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('shopify:section:unload', unload);
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width;
          if (Number.isFinite(width) && Math.abs(width - previousWidth) > .5) { previousWidth = width; schedule(); }
        });
        observer.observe(description);
      }
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(schedule);
        mutationObserver.observe(description, { childList: true, characterData: true, subtree: true });
      }
      document.fonts?.addEventListener?.('loadingdone', schedule);
      document.fonts?.ready.then(schedule).catch(() => {});
      root.setAttribute('data-acropoli-expand-initialized', 'true');
      guardedUpdate();
    } catch { failOpen(); }
  }

  function initialize(scope = document) {
    if (scope.matches?.(selector)) enhance(scope);
    scope.querySelectorAll(selector).forEach(enhance);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  window.addEventListener('load', () => initialize(), { once: true });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
